// Page metadata for search engines and AI crawlers (spec section 37):
// <title>, meta description, canonical URL, Open Graph / Twitter cards, and JSON-LD structured data.
import { mediaUrl } from '../api/contentApi';
import { plainText } from './renderer/Inlines';

import { cfg } from '../../runtimeConfig';

const SITE_URL = (cfg('SITE_URL', process.env.REACT_APP_SITE_URL) || window.location.origin).replace(/\/$/, '');

function absolute(url) {
  if (!url) return null;
  return /^https?:\/\//.test(url) ? url : `${SITE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
}

function setMeta(key, content, attr = 'name') {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!content) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.dataset.managed = 'seo';
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

function setJsonLd(data) {
  document.head.querySelectorAll('script[data-seo="jsonld"]').forEach((el) => el.remove());
  for (const item of data) {
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.dataset.seo = 'jsonld';
    el.textContent = JSON.stringify(item);
    document.head.appendChild(el);
  }
}

/** Questions/answers from a "Frequently asked questions" / "FAQ" section: sub-headings followed by text. */
export function extractFaq(blocks) {
  const start = blocks.findIndex(
    (b) => b.type === 'heading' && /^(frequently asked questions|faqs?)$/i.test(plainText(b.children).trim()),
  );
  if (start < 0) return [];
  const sectionLevel = blocks[start].level;
  const faq = [];
  let current = null;
  for (const b of blocks.slice(start + 1)) {
    if (b.type === 'heading' && b.level <= sectionLevel) break;
    if (b.type === 'heading') {
      current = { question: plainText(b.children).trim(), answer: [] };
      faq.push(current);
    } else if (current && (b.type === 'paragraph' || b.type === 'quote')) {
      current.answer.push(plainText(b.children).trim());
    }
  }
  return faq.filter((q) => q.question && q.answer.length);
}

export function applySeo({ doc, entry, media, siteName, path }) {
  const meta = doc.meta || {};
  const seo = doc.seo || {};
  const url = seo.canonicalUrl || `${SITE_URL}${path}`;
  const title = seo.metaTitle || meta.title;
  const description = seo.metaDescription || meta.excerpt || '';
  const imageRecord = media?.[seo.ogImageMediaId || meta.featuredMediaId];
  const image = absolute(mediaUrl(imageRecord));
  const published = entry?.sourceCreated || entry?.importedAt;
  const modified = entry?.sourceModified || entry?.importedAt;

  document.title = `${title} | ${siteName}`;
  setMeta('description', description);
  setMeta('keywords', (meta.tags || []).join(', '));
  setMeta('author', meta.authorName);
  setMeta('robots', 'index, follow, max-image-preview:large');
  setLink('canonical', url);

  setMeta('og:type', 'article', 'property');
  setMeta('og:site_name', siteName, 'property');
  setMeta('og:url', url, 'property');
  setMeta('og:title', seo.ogTitle || title, 'property');
  setMeta('og:description', seo.ogDescription || description, 'property');
  setMeta('og:image', image, 'property');
  setMeta('og:image:alt', imageRecord?.altText, 'property');
  setMeta('article:published_time', published, 'property');
  setMeta('article:modified_time', modified, 'property');
  setMeta('article:section', meta.categoryName, 'property');
  setMeta('twitter:card', image ? 'summary_large_image' : 'summary');
  setMeta('twitter:title', seo.ogTitle || title);
  setMeta('twitter:description', seo.ogDescription || description);
  setMeta('twitter:image', image);

  const article = {
    '@context': 'https://schema.org',
    '@type': doc.type === 'blog' ? 'BlogPosting' : 'Article',
    headline: meta.title,
    description,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    url,
    image: image ? [image] : undefined,
    datePublished: published,
    dateModified: modified,
    author: meta.authorName ? { '@type': 'Person', name: meta.authorName } : undefined,
    publisher: { '@type': 'Organization', name: siteName },
    articleSection: meta.categoryName || undefined,
    keywords: (meta.tags || []).join(', ') || undefined,
    about: seo.focusKeyword || undefined,
    wordCount: entry?.wordCount,
    inLanguage: document.documentElement.lang || 'en',
  };
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: siteName, item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: meta.title, item: url },
    ],
  };
  const data = [article, breadcrumb];
  const faq = extractFaq(doc.content || []);
  if (faq.length) {
    data.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faq.map((q) => ({
        '@type': 'Question',
        name: q.question,
        acceptedAnswer: { '@type': 'Answer', text: q.answer.join(' ') },
      })),
    });
  }
  setJsonLd(data);
}

export function applyListingSeo({ siteName, description }) {
  document.title = siteName;
  setMeta('description', description);
  setMeta('robots', 'index, follow');
  setLink('canonical', `${SITE_URL}/insights`);
  setMeta('og:type', 'website', 'property');
  setMeta('og:title', siteName, 'property');
  setMeta('og:description', description, 'property');
  setMeta('og:url', `${SITE_URL}/insights`, 'property');
  ['og:image', 'og:image:alt', 'article:published_time', 'article:modified_time', 'article:section',
    'twitter:image'].forEach((k) => setMeta(k, null, k.startsWith('twitter') ? 'name' : 'property'));
  setMeta('keywords', null);
  setMeta('author', null);
  setJsonLd([
    { '@context': 'https://schema.org', '@type': 'WebSite', name: siteName, url: `${SITE_URL}/` },
  ]);
}

/** Preview pages are private: tell search engines and AI crawlers to ignore them and drop the public metadata. */
export function applyPreviewSeo(title, siteName) {
  document.title = `Preview: ${title} | ${siteName}`;
  setMeta('robots', 'noindex, nofollow, noarchive, noimageindex');
  document.head.querySelector('link[rel="canonical"]')?.remove();
  ['description', 'keywords', 'author', 'twitter:card', 'twitter:title', 'twitter:description', 'twitter:image'].forEach(
    (k) => setMeta(k, null),
  );
  ['og:type', 'og:site_name', 'og:url', 'og:title', 'og:description', 'og:image', 'og:image:alt',
    'article:published_time', 'article:modified_time', 'article:section'].forEach((k) => setMeta(k, null, 'property'));
  setJsonLd([]);
}

/** Back on a non-article page of the website: drop everything the article pages put in <head>. */
export function resetSeo(title, description) {
  document.title = title;
  setMeta('description', description);
  setMeta('robots', 'index, follow');
  document.head.querySelector('link[rel="canonical"]')?.remove();
  ['keywords', 'author', 'twitter:card', 'twitter:title', 'twitter:description', 'twitter:image'].forEach((k) => setMeta(k, null));
  ['og:type', 'og:site_name', 'og:url', 'og:title', 'og:description', 'og:image', 'og:image:alt',
    'article:published_time', 'article:modified_time', 'article:section'].forEach((k) => setMeta(k, null, 'property'));
  setJsonLd([]);
}
